import { ContactTypeEnum } from '@ben/api/gen/codegen.schemas';
import RiInformationFill from '@ben/icons/information-fill';
import { RIcon } from '@ben/orion/remix-icon/remix-icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { RestrictionsByType } from '../../../types/columbia';
import type { AcceptedContact } from '@ben/shared/utils/contacts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';

interface ContactSelectionInfoProps {
  max?: number;
  min?: number;
  restrictionsByType: RestrictionsByType;
  acceptedContactTypes?: AcceptedContact[];
}

const ContactSelectionInfo = ({
  max,
  restrictionsByType,
  acceptedContactTypes,
}: ContactSelectionInfoProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const allContactTypes = useMemo(
    () => [
      {
        value: ContactTypeEnum.SELF,
        label: t('profile.dependents.relationships.self'),
      },
      {
        value: ContactTypeEnum.SPOUSE,
        label: t('profile.dependents.relationships.spouse'),
      },
      {
        value: ContactTypeEnum.CHILD,
        label: t('profile.dependents.relationships.child'),
      },
      {
        value: ContactTypeEnum.FRIEND,
        label: t('profile.dependents.relationships.friend'),
      },
      {
        value: ContactTypeEnum.OTHER,
        label: t('profile.dependents.relationships.other'),
      },
    ],
    [t]
  );

  const maxMinInfoText = useMemo(() => {
    if (max) {
      return t('benefits.maxDependents', { count: max });
    }
    return '';
  }, [max, t]);

  const maxByTypeInfoText = useMemo(() => {
    const typeLimits: string[] = [];

    for (const [key, value] of Object.entries(restrictionsByType)) {
      if (typeof value.maxSelections === 'number') {
        const label = t(`benefits.${key.toLowerCase()}`, {
          count: value.maxSelections,
        });
        typeLimits.push(label);
      }
    }

    if (typeLimits.length === 0) return '';

    const typeLimitText = typeLimits.join(` ${t('words.and')} `);

    return t('benefits.selectUpTo', { limits: typeLimitText });
  }, [restrictionsByType, t]);

  const acceptedContactTypesText = useMemo(() => {
    if (!acceptedContactTypes) return '';

    const acceptedTypeSet = new Set(acceptedContactTypes);

    const acceptedTypes = allContactTypes
      .filter((contactType) =>
        acceptedTypeSet.has(contactType.value as unknown as AcceptedContact)
      )
      .map((contactType) => contactType.label)
      .join(', ');

    return t('benefits.acceptedContactTypes', { limits: acceptedTypes });
  }, [acceptedContactTypes, allContactTypes, t]);

  return (
    <InfoWrapper>
      {acceptedContactTypesText && (
        <MaxSelectionWrapper>
          <RIcon icon={RiInformationFill} color={theme.palette.neutral[90]} />
          <Typography
            variant={TypographyVariant.bodySm1}
            color={theme.palette.neutral[90]}
          >
            {acceptedContactTypesText}
          </Typography>
        </MaxSelectionWrapper>
      )}
      {(maxMinInfoText || maxByTypeInfoText) && (
        <MaxSelectionWrapper>
          <RIcon icon={RiInformationFill} color={theme.palette.neutral[90]} />
          <Typography
            variant={TypographyVariant.bodySm1}
            color={theme.palette.neutral[90]}
          >
            {maxMinInfoText} {maxByTypeInfoText}
          </Typography>
        </MaxSelectionWrapper>
      )}
    </InfoWrapper>
  );
};

export default ContactSelectionInfo;

const InfoWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `}
`;

const MaxSelectionWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `}
`;
